<template>
    <div class="flex flex-col gap-4 h-full overflow-y-auto pt-20 md:pt-20 xl:pt-8  p-4 md:p-8">
        <div class="flex flex-col w-full gap-4">
            <div class="flex flex-col gap-1">
                <h1 class="text-xl font-modulus-semibold font-[500]">{{ $t("dashboard.auto.resumen_este_mes") }}</h1>
                <p class="text-sm font-modulus font-[300]">{{ $t("dashboard.auto.muestra_a_manera_de_resumen_todo_lo_que_se_ha_re") }}</p>
            </div>

            <div :class="['grid gap-4 sm:gap-5 lg:gap-6 grid-cols-1 sm:grid-cols-2', hasFinancialKpis ? 'md:grid-cols-3 lg:grid-cols-5' : '']">
                <div data-test="kpi-total-bookings" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.total_de_reservas_recibidas") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                                    <IconCalendar class="h-5 w-5 text-blue-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.totalBooking ?? 0 }}</div>
                    </div>
                </div>

                <div data-test="kpi-today-bookings" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.reservas_de_hoy") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-rajah-100">
                                    <IconCalendar class="h-5 w-5 text-rajah-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.todayBookings ?? 0 }}</div>
                    </div>
                </div>

                <div v-if="monthSummary.totalBilled !== null" data-test="kpi-realized-income" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.ingreso_realizado") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-eblack-100">
                                    <IconCashBanknote class="h-5 w-5 text-eblack-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.totalBilled }}</div>
                    </div>
                </div>

                <div v-if="monthSummary.totalIncomeThisMonth !== null" data-test="kpi-realized-income-this-month" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.ingreso_realizado_este_mes") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-egreen-100">
                                    <IconCircleCheck class="h-5 w-5 text-egreen-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.totalIncomeThisMonth }}</div>
                    </div>
                </div>

                <div v-if="monthSummary.approvedIncomeThisMonth !== null" data-test="kpi-income-receivable" class="border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="pb-3">
                        <div class="flex items-center justify-between">
                            <div class="text-lg font-medium text-muted-foreground text-eblack-800">{{ $t("dashboard.auto.por_cobrar") }}</div>
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-rajah-100">
                                    <IconCircleCheck class="h-5 w-5 text-rajah-600" />
                                </div>
                            </div>
                        </div>
                    <div>
                        <div class="text-3xl font-bold text-foreground text-eblack-900">{{ monthSummary.approvedIncomeThisMonth }}</div>
                    </div>
                </div>
            </div>

            <div :class="['grid gap-4 sm:gap-5 lg:gap-6 grid-cols-1', generalSummary ? 'lg:grid-cols-2' : '']">
                <div v-if="generalSummary" data-test="general-summary" class="border shadow-sm p-4 rounded-2xl">
                    <div>
                        <div class="flex items-center justify-between">
                            <div>
                                <h2 class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.resumen_general") }}</h2>
                                <p class="mt-1">{{ $t("dashboard.auto.muestra_a_manera_de_resumen_todo_lo_que_se_ha_re_i7ya5") }}</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div class="space-y-4">
                            <div class="flex items-center justify-between py-3 border-b border-border">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.total_de_reservas_recibidas") }}</span>
                                <span class="text-2xl font-bold text-foreground">{{ generalSummary.totalBooking }}</span>
                            </div>

                            <div class="flex items-center justify-between py-3 border-b border-border">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.ingreso_realizado_total") }}</span>
                                <span class="text-2xl font-bold text-foreground">${{ generalSummary.totalBilled }}</span>
                            </div>

                            <div class="flex items-center justify-between py-3">
                                <span class="text-sm font-medium text-muted-foreground">{{ $t("dashboard.auto.reservaciones_canceladas") }}</span>
                                <span class="text-2xl font-bold text-foreground">{{ generalSummary.cancelledBookings }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div data-test="upcoming-bookings-section" class="border shadow-sm p-4 rounded-2xl">
                    <div>
                        <div class="flex items-center justify-between">
                            <div>
                                <h2 class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.reservas_proximas") }}</h2>
                                <p class="mt-1">{{ $t("dashboard.auto.te_permite_visualizar_las_diferentes_reservas_qu") }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Cargar las reservas próximamente (comingBookings) -->
                    <div v-if="upcomingBookings.length > 0" class="w-full mt-4">
                        <div class="grid grid-cols-1 md:grid-cols-2">
                            <div v-for="booking in upcomingBookings" :key="booking.id" class="mb-4 p-4 border rounded-lg">
                                <div class="flex flex-col">
                                    <span class="font-medium text-eblack-800">{{ booking.customer_name }}</span>
                                    <span class="text-sm text-muted-foreground">{{ $t("dashboard.auto.start_label") }}: {{ formatDate(booking.start_datetime) }}</span>
                                    <span class="text-sm text-muted-foreground">{{ $t("dashboard.auto.end_label") }}: {{ formatDate(booking.end_datetime) }}</span>
                                    <span class="text-sm text-muted-foreground">{{ passengerCountLabel(booking) }}</span>
                                    <span class="text-md font-semibold mt-2">${{ formatCurrency(booking.subtotal) }}</span>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div v-else>
                        <div class="flex flex-col items-center justify-center py-12">
                            <div class="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
                                <IconCalendar class="h-8 w-8 text-slate-400" />
                            </div>
                            <p class="text-sm text-muted-foreground">{{ $t("dashboard.auto.aun_no_hay_reservas") }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="commissionsAvailable" data-test="services-comparison-section" class="border shadow-sm p-4 rounded-2xl">
                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <div class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.comparacion_de_servicios") }}</div>
                        </div>
                            <DashboardDropdown :options="yearsDropdownOptions"/>
                        </div>
                    </div>
                <div>
                    <DashboardVerticalBarChart :data="chartVerticalData" />
                </div>
            </div>

            <div v-if="commissionsAvailable" data-test="income-section" class="border shadow-sm p-4 rounded-2xl">
                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <div class="text-lg font-semibold text-eblack-800">{{ $t("dashboard.auto.ingreso_realizado") }}</div>
                        </div>
                            <DashboardDropdown :options="yearsDropdownOptions"/>
                        </div>
                    </div>
                <div>
                    
                    <DashboardChart  :data="chartData" />
                </div>
            </div>
        </div>
    </div>

    <!-- <div class="flex flex-col gap-4 h-full overflow-y-auto">
        <div class="flex flex-col w-full  gap-4">
            <div class="flex flex-col gap-1">
                <h1 class="text-lg">{{ $t("dashboard.auto.resumen_este_mes") }}</h1>
                <p class="text-sm">{{ $t("dashboard.auto.muestra_a_manera_de_resumen_todo_lo_que_se_ha_re") }}</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 p-4 border rounded-md bg-white">
                <div class="flex flex-col gap-2">
                    <h2 class="text-lg">{{ $t("dashboard.auto.total_de_reservas_recibidas") }}</h2>
                    <p class="text-2xl">{{ monthSummary.totalBooking }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <h2 class="text-lg">{{ $t("dashboard.auto.reservas_de_hoy") }}</h2>
                    <p class="text-2xl">{{ monthSummary.todayBookings }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <h2 class="text-lg">{{ $t("dashboard.auto.ingresos_generados") }}</h2>
                    <p class="text-2xl">${{ monthSummary.totalBilled }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <h2 class="text-lg">{{ $t("dashboard.auto.ingresos_pagados") }}</h2>
                    <p class="text-2xl">${{ monthSummary.totalIncomeThisMonth }}</p>
                </div>
                <div class="flex flex-col gap-2">
                    <h2 class="text-lg">{{ $t("dashboard.auto.ingresos_pendientes") }}</h2>
                    <p class="text-2xl">${{ monthSummary.approvedIncomeThisMonth }}</p>
                </div>
            </div>
        </div>

        <div class="flex justify-between gap-1">
            <h1 class="text-lg">{{ $t("dashboard.auto.comparacion_de_servicios") }}</h1>
            <DashboardDropdown 
            :options="yearsDropdownOptions"/>
        </div>

        <div class="flex relative w-full h-96">
            <DashboardVerticalBarChart :data="chartVerticalData" />
        </div>

        <div class="flex justify-between gap-1">
            <h1 class="text-lg">{{ $t("dashboard.auto.ingresos") }}</h1>
            <DashboardDropdown 
            :options="yearsDropdownOptions"/>
        </div>

        <div class="flex relative w-full h-96">
            <DashboardChart  :data="chartData" />
        </div>

        <div class="grid grid-cols-3 mt-4 gap-4">
            <div class="flex flex-col gap-4 p-4 ">
                <div class="flex flex-col gap-1">
                    <h1 class="text-lg">{{ $t("dashboard.auto.resumen_general") }}</h1>
                    <p class="text-sm">{{ $t("dashboard.auto.muestra_a_manera_de_resumen_todo_lo_que_se_ha_re_i7ya5") }}</p>
                </div>
                <div class="flex flex-col border rounded-md bg-white gap-4 p-4 flex-1">
                    <div class="flex flex-col gap-2">
                        <h2 class="text-lg">{{ $t("dashboard.auto.total_de_reservas_recibidas") }}</h2>
                        <p class="text-2xl">{{ generalSummary.totalBooking }}</p>
                    </div>
                    <div class="flex flex-col gap-2">
                        <h2 class="text-lg">{{ $t("dashboard.auto.ingresos_generados") }}</h2>
                        <p class="text-2xl">${{ generalSummary.totalBilled }}</p>
                    </div>
                    <div class="flex flex-col gap-2">
                        <h2 class="text-lg">{{ $t("dashboard.auto.reservaciones_canceladas") }}</h2>
                        <p class="text-2xl">{{ generalSummary.cancelledBookings }}</p>
                    </div>
                </div>
            </div>

            <div class="col-span-2 h-full">
                <div class="w-full flex flex-col p-4 gap-4 h-full">
                    <div class="flex flex-col gap-1">
                        <h1 class="text-lg">{{ $t("dashboard.auto.reservas_proximas") }}</h1>
                        <p class="text-sm">{{ $t("dashboard.auto.te_permite_visualizar_las_diferentes_reservas_qu") }}</p>
                    </div>

                    <div class="border rounded-md bg-white p-4 h-[354px] overflow-hidden flex flex-col gap-2">
                         <div class="flex justify-end sticky right-0 top-0">
                            <DashboardDropdown 
                            :options="stateDropdownOptions"/>
                        </div>
                        <div class="flex-1 flex flex-col gap-4 overflow-y-auto" v-if="bookings.length > 0">
                            <div class="flex flex-col w-full border rounded-lg p-2" v-for="booking in bookings" :key="booking.id"> 
                                <div class="flex justify-between items-center" >
                                    <div class="flex flex-col gap-1">
                                        <h1>{{ booking.customer_name }}</h1>
                                        <div class="flex flex-col gap-2">
                                            <span class="text-md font-semibold">${{ booking.total }}</span>
                                            <span class="text-sm">
                                                {{ $t("dashboard.auto.start_label") }}: {{ formatDate(booking.start_datetime) }} -
                                                {{ $t("dashboard.auto.end_label") }}: {{ formatDate(booking.end_datetime) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col w-full   rounded-lg p-2" v-else> 
                            <div class="flex justify-center items-center" >
                                <span class="text-sm text-gray-500">{{ $t("dashboard.auto.aun_no_hay_reservas") }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div> -->
</template>

<script>
import DashboardChart from './DashboardChart.vue';
import DashboardVerticalBarChart from './DashboardVerticalBarChart.vue';
import dashboardRequests from '@/services/admin/dashboard-service';
import DashboardDropdown from './DashboardDropdown.vue';
import { IconCalendar, IconCashBanknote, IconCircleCheck } from '@tabler/icons-vue';
export default {
    name:'ProviderDashboard',
    data(){
        return {
            chartData:[
                {
                    label: '',
                    backgroundColor: '#F3764A',
                    data: []
                },
            ],
            chartVerticalData:[],
            currentYear: new Date().getFullYear(),
            todayBookings:[],
            upcomingBookings:[],
            // Financial fields stay null until the API returns them; the API
            // withholds them (null) for members without org financial access.
            monthSummary: {
                totalBooking:0,
                todayBookings:0,
                totalBilled:null,
                totalIncomeThisMonth:null,
                approvedIncomeThisMonth:null
            },
            generalSummary: null,
            commissionsAvailable: false,
            providerDni:'',
            servicesCommissions:[],
            incomes:[],
            bookings:[],
            yearsDropdownOptions: {
                values:[2025,2026,2027],
                default: new Date().getFullYear()
            },
        }
    },
    computed: {
        hasFinancialKpis() {
            return this.monthSummary.totalBilled !== null
                || this.monthSummary.totalIncomeThisMonth !== null
                || this.monthSummary.approvedIncomeThisMonth !== null;
        },
        currentLocale() {
            const locale = this.$i18n?.locale;
            return typeof locale === "string" ? locale : locale?.value || "es";
        },
        stateDropdownOptions() {
            return {
                values: [
                    this.$t("dashboard.auto.dropdown.upcoming"),
                    this.$t("dashboard.auto.dropdown.today"),
                ],
                default: this.$t("dashboard.auto.dropdown.today"),
            };
        },
    },
    components:{
        DashboardChart,
        DashboardVerticalBarChart,
        DashboardDropdown,
        IconCalendar,
        IconCashBanknote,
        IconCircleCheck
    },
    watch: {
        "$i18n.locale"() {
            this.prepareChartData();
        },
    },
    methods:{
        getMetrics(){
            dashboardRequests.getCurrentProviderMetrics().then(
                res => {
                    if(res.status == 200){
                        let {data} = res
                        let {comingBookings = [], generalSummary, monthlySummary, todayBookings = []} = data

                        this.monthSummary.totalBooking = monthlySummary.itineraries_this_month;
                        this.monthSummary.todayBookings = todayBookings.length;
                        this.monthSummary.totalBilled = monthlySummary.total_billed ?? null;
                        this.monthSummary.totalIncomeThisMonth = monthlySummary.total_billed_this_month ?? null;
                        this.monthSummary.approvedIncomeThisMonth = monthlySummary.approved_billed_this_month ?? null;

                        this.generalSummary = generalSummary
                            ? {
                                totalBooking: generalSummary.itineraries_this_month,
                                totalBilled: generalSummary.total_billed,
                                cancelledBookings: generalSummary.total_cancelled,
                            }
                            : null

                        this.upcomingBookings = comingBookings.map(this.normalizeDashboardBooking);
                        this.todayBookings = todayBookings.map(this.normalizeDashboardBooking);
                        this.bookings = [...this.todayBookings]

                    }
                }
            ).catch(error => {
                console.log(error)
            })
        },
        getCommissions(){
            dashboardRequests.getCurrentProviderCommissionsMetrics(this.currentYear).then(
                res => {
                    if(res.status == 200){
                       let {servicesCommissions,incomes} = res.data
                       this.commissionsAvailable = Array.isArray(servicesCommissions) && Array.isArray(incomes)
                       this.servicesCommissions = servicesCommissions || []
                       this.incomes = incomes || []
                       this.prepareChartData()
                    }
                }
            ).catch(error => {
                console.log(error)
            })
        },
        initData(){
            this.getMetrics()
            this.getCommissions()
        }, 
        prepareChartData(){
            let incomesByMonth = [];
            let services = [];
            let incomes = [];

            this.incomes.forEach(item => {
                incomesByMonth.push(item.monthly_commission)
            })

            this.servicesCommissions.forEach(item => {
                services.push(item.services_amount)
                incomes.push(item.monthly_commission)
            })

            let servicesAmount = {
                label: this.$t("dashboard.auto.chart_services_count"),
                backgroundColor: '#03a8b7',
                data: services
            };

            let servicesIncome = {
                label: this.$t("dashboard.auto.chart_service_income"),
                backgroundColor: '#F3764A',
                data: incomes
            }
            this.chartVerticalData = [servicesAmount, servicesIncome]
            this.chartData[0].label = this.$t("dashboard.auto.chart_realized_income")
            this.chartData[0].data = incomesByMonth
        },
        normalizeDashboardBooking(booking) {
            const startDateTime = booking.start_datetime || booking.first_start_datetime || booking.start_date || null;
            const endDateTime = booking.end_datetime || booking.first_end_datetime || booking.end_date || startDateTime;
            const providerAmount = booking.provider_facing_amount ?? booking.subtotal ?? 0;

            return {
                ...booking,
                start_datetime: startDateTime,
                end_datetime: endDateTime,
                subtotal: Number(providerAmount),
                total: Number(booking.total ?? booking.total_amount ?? 0),
            };
        },
        formatCurrency(value) {
            const amount = Number(value ?? 0);
            if (!Number.isFinite(amount)) return "0.00";

            return amount.toLocaleString(this.currentLocale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        formatDate(dateString) {
            if (!dateString) return this.$t("dashboard.auto.no_date");
            let date = new Date(dateString);
            if (Number.isNaN(date.getTime())) return this.$t("dashboard.auto.no_date");

            return new Intl.DateTimeFormat(this.currentLocale, {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: "UTC",
            }).format(date);
        },
        passengerCountLabel(booking) {
            const count =
                booking.quantity_of_participants ??
                booking.participants ??
                booking.passengers ??
                0;
            return this.$t("dashboard.auto.passenger_count", { count });
        }
    },
    mounted(){
        this.initData()
    }
}
</script>

<style lang="scss" scoped>

</style>
